import { formatNumber, useTranslate } from '@si/utils';
import type { JSX } from 'react';

import { Divider } from '../Divider/Divider';
import { List } from '../List/List';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';
import { useColors } from '../ThemeProvider';

interface Props {
  items: SummaryListItemType[];
  hasDividers?: boolean;
  color?: 'primary' | 'black' | 'white';
}

export function SummaryList({ items, hasDividers = false, color = 'primary' }: Props) {
  const colors = useColors();

  return (
    items.length > 0 && (
      <List>
        {items.map(({ label, value }) => (
          <Stack
            key={JSON.stringify({ label, value: value?.toString() })}
            gap={hasDividers ? 1 : 0}
            mt={hasDividers ? 1 : 0}
          >
            <SummaryListItem label={label} value={value} color={color} />

            {hasDividers && <Divider color={colors[color]} />}
          </Stack>
        ))}
      </List>
    )
  );
}

export interface SummaryListItemType {
  label: string;
  value: string | string[] | number | number[] | boolean | JSX.Element | undefined | null;
  color?: 'primary' | 'black' | 'white';
}

export function SummaryListItem({ label, value, color = 'primary' }: SummaryListItemType) {
  const { t, translate } = useTranslate();

  if (typeof value === 'boolean') value = translate(value ? t.yes : t.no);
  if (value === '') value = '-';
  if (Array.isArray(value)) value = value.join(', ');
  if (value === undefined || value === null) return null;
  if (typeof value === 'number') value = formatNumber(value);

  return (
    <Stack className="summary-list-item" direction="row" flexWrap="wrap">
      <Text color={color} bold>{`${label}: `}</Text>
      {typeof value === 'string' ? <Text color={color}>{value}</Text> : value}
    </Stack>
  );
}
